import React from 'react';
import { MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import { EmergencyCta, SectionHeading } from '@/components/sections';
import { IMAGES, BRAND } from '@/lib/site';

const AREAS = [
  {
    slug: 'coquitlam',
    name: 'Coquitlam',
    imageAlt: 'Coquitlam Town Centre skyline with the North Shore Mountains',
    copy: 'Water damage help is available throughout Coquitlam. Coquitlam includes a mix of detached homes, townhomes and apartments, so the response should be based on the water source, the materials affected and how far moisture has travelled.',
    neighborhoods: ['Burke Mountain', 'Westwood Plateau', 'Town Centre', 'Austin Heights', 'Maillardville', 'Central Coquitlam', 'Burquitlam', 'Eagle Ridge'],
  },
  {
    slug: 'port-coquitlam',
    name: 'Port Coquitlam',
    imageAlt: 'Port Coquitlam West Coast Express station',
    copy: 'Water damage help is available throughout Port Coquitlam. Homes across the city can be affected by burst pipes, plumbing leaks, appliance failures, lower-level flooding and other water emergencies.',
    neighborhoods: ['Citadel Heights', 'Riverwood', 'Glenwood', 'Lincoln Park', 'Mary Hill', 'Oxford Heights', 'Birchland Manor', 'Central Port Coquitlam'],
  },
  {
    slug: 'port-moody',
    name: 'Port Moody',
    imageAlt: 'Aerial view of Port Moody Centre and Rocky Point Park',
    copy: 'Water damage help is available throughout Port Moody. From older homes to newer townhomes and multi-unit residences, the right response depends on the source of the water and the materials affected.',
    neighborhoods: ['Moody Centre', 'Heritage Mountain', 'Heritage Woods', 'Noons Creek', 'Newport Village', 'Suter Brook Village', 'College Park', 'Ioco'],
  },
];

export default function AreasPage() {
  return <>
    <Seo
      title="Water Damage Service Areas | Coquitlam, Port Coquitlam & Port Moody"
      description="Water damage help across Coquitlam, Port Coquitlam and Port Moody. See the Tri-Cities neighbourhoods commonly served."
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Tri-Cities Water Damage Help',
          serviceType: 'Water damage restoration help',
          provider: { '@type': 'Organization', name: BRAND },
          areaServed: [
            { '@type': 'City', name: 'Coquitlam', addressRegion: 'BC', addressCountry: 'CA' },
            { '@type': 'City', name: 'Port Coquitlam', addressRegion: 'BC', addressCountry: 'CA' },
            { '@type': 'City', name: 'Port Moody', addressRegion: 'BC', addressCountry: 'CA' },
          ],
        },
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Areas', path: '/areas' }]),
      ]}
    />

    <section className="relative overflow-hidden bg-navy">
      <img src={IMAGES.locations['port-moody']} alt="Tri-Cities British Columbia service area" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
      <div className="container-page relative py-16 sm:py-20">
        <span className="inline-block rounded-full bg-water/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-water/20">Areas We Serve</span>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">Water Damage Help Across the <span className="text-water">Tri-Cities</span></h1>
        <p className="mt-4 max-w-2xl text-lg text-white/70">One service area covering Coquitlam, Port Coquitlam and Port Moody.</p>
      </div>
    </section>

    <section className="py-16 sm:py-20">
      <div className="container-page">
        <SectionHeading center eyebrow="Local Coverage" title="Serving Homes Across the Tri-Cities" sub="Find your city below and see the neighbourhoods commonly served." />

        <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
          <div className="grid lg:grid-cols-[1.25fr_.75fr]">
            <iframe title="Tri-Cities BC service area map" src="https://www.google.com/maps?q=Coquitlam%20BC&z=11&output=embed" className="h-[340px] w-full border-0 sm:h-[390px]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="flex flex-col justify-center bg-navy-deep p-7 text-white sm:p-9">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-water">Tri-Cities Service Area</span>
              <h2 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl">Coquitlam, Port Coquitlam & Port Moody</h2>
              <p className="mt-4 text-sm leading-7 text-white/70">Our service area is intentionally focused on these three neighbouring cities so requests can be routed locally.</p>
              <ul className="mt-5 space-y-3">{['Coquitlam','Port Coquitlam','Port Moody'].map(city=><li key={city} className="flex items-center gap-2 text-sm font-semibold"><MapPin className="h-4 w-4 text-water"/> {city}</li>)}</ul>
            </div>
          </div>
        </div>

        <div className="mt-12 space-y-14 sm:space-y-16">
          {AREAS.map((area, index) => (
            <article id={area.slug} key={area.slug} className={`scroll-mt-24 grid gap-8 rounded-3xl border border-border bg-white p-6 shadow-sm sm:p-8 lg:grid-cols-2 lg:items-center ${index % 2 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
              <div className="overflow-hidden rounded-2xl bg-secondary">
                <img
                  src={IMAGES.locations[area.slug]}
                  alt={area.imageAlt}
                  className="h-64 w-full object-cover sm:h-80"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-water-strong">Local Coverage</span>
                <h2 className="mt-2 font-display text-3xl font-extrabold text-primary">Serving Homes Across {area.name}</h2>
                <p className="mt-4 leading-relaxed text-muted-foreground">{area.copy}</p>

                <h3 className="mt-7 font-display text-lg font-bold text-primary">Areas Commonly Served</h3>
                <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" aria-label={`Neighbourhoods commonly served in ${area.name}`}>
                  {area.neighborhoods.map((n) => (
                    <li key={n} className="flex items-center gap-2 text-sm font-medium text-primary">
                      <MapPin className="h-4 w-4 shrink-0 text-water-strong" aria-hidden="true" />
                      {n}
                    </li>
                  ))}
                </ul>
                <Link to={`/${area.slug}`} className="mt-7 inline-flex items-center gap-1 rounded-full bg-navy-deep px-5 py-2.5 text-sm font-bold text-white transition hover:bg-navy">
                  Water damage help in {area.name} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-8 text-center text-[10px] leading-relaxed text-muted-foreground/70">
          Location photos: Coquitlam — Greg Salter (CC BY-SA 3.0); Port Coquitlam — Richard Eriksson (CC BY 2.0); Port Moody — Northwest (CC BY-SA 4.0), via Wikimedia Commons.
        </p>
      </div>
    </section>

    <EmergencyCta />
  </>;
}
